import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Save, Image, Palette, Type, Wrench, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardSiteSettings() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState(null);
  const [services, setServices] = useState([]);
  const [expandedService, setExpandedService] = useState(null);
  const [savingService, setSavingService] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    loadContent();
    loadServices();
  }, [navigate]);

  const loadContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/site-content`);
      setContent(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const loadServices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error loading services:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/site-content`, content, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Impostazioni salvate!');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleServiceUpdate = async (serviceId, idx) => {
    setSavingService(idx);
    try {
      const token = localStorage.getItem('token');
      const svc = services[idx];
      await axios.put(`${BACKEND_URL}/api/services/${serviceId}`, {
        name: svc.name,
        description: svc.description,
        description_en: svc.description_en,
        icon: svc.icon,
        price_range: svc.price_range,
        duration_minutes: svc.duration_minutes
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Servizio "${svc.name}" aggiornato!`);
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSavingService(null);
    }
  };

  const updateService = (idx, field, value) => {
    setServices(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#0066b2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8" data-testid="dashboard-site-settings">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>{t('dashboard', 'siteContent')}</h1>
              <p className="text-gray-600">{t('dashboard', 'siteContentDesc')}</p>
            </div>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0066b2] hover:bg-[#005494]" data-testid="save-content-button">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvataggio...' : 'Salva Modifiche'}
            </Button>
          </div>

          <Tabs defaultValue="texts" className="space-y-6">
            <TabsList className="bg-white border border-gray-200">
              <TabsTrigger value="texts" className="flex items-center gap-2"><Type className="w-4 h-4" /> Testi</TabsTrigger>
              <TabsTrigger value="services" className="flex items-center gap-2"><Wrench className="w-4 h-4" /> Servizi</TabsTrigger>
              <TabsTrigger value="images" className="flex items-center gap-2"><Image className="w-4 h-4" /> Immagini</TabsTrigger>
              <TabsTrigger value="colors" className="flex items-center gap-2"><Palette className="w-4 h-4" /> Colori</TabsTrigger>
            </TabsList>

            {/* Texts Tab */}
            <TabsContent value="texts" className="space-y-6">
              <div className="bg-white rounded-2xl p-6 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Sezione Hero</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Titolo Principale (IT)</label>
                    <Input value={content?.hero_title || ''} onChange={(e) => setContent({ ...content, hero_title: e.target.value })} placeholder="Oltre 50 Anni di Eccellenza..." data-testid="hero-title-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sottotitolo / Descrizione (IT)</label>
                    <Textarea value={content?.hero_subtitle || ''} onChange={(e) => setContent({ ...content, hero_subtitle: e.target.value })} rows={5} data-testid="hero-subtitle-input" />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Statistiche</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Anni di Esperienza</label>
                    <Input value={content?.stat_years || ''} onChange={(e) => setContent({ ...content, stat_years: e.target.value })} placeholder="50+" data-testid="stat-years-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Clienti Soddisfatti</label>
                    <Input value={content?.stat_customers || ''} onChange={(e) => setContent({ ...content, stat_customers: e.target.value })} placeholder="90000+" data-testid="stat-customers-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Servizi Specializzati</label>
                    <Input value={content?.stat_services || ''} onChange={(e) => setContent({ ...content, stat_services: e.target.value })} placeholder="10+" data-testid="stat-services-input" />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Services Tab */}
            <TabsContent value="services" className="space-y-4">
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <p className="text-sm text-blue-800">Modifica le descrizioni dei servizi in italiano e inglese. Ogni servizio ha un pulsante "Salva" indipendente.</p>
              </div>
              {services.map((svc, idx) => (
                <div key={svc.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid={`cms-service-${idx}`}>
                  <button
                    onClick={() => setExpandedService(expandedService === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Wrench className="w-5 h-5 text-[#0066b2]" />
                      <span className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>{svc.name}</span>
                    </div>
                    {expandedService === idx ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </button>
                  {expandedService === idx && (
                    <div className="px-5 pb-5 space-y-4 border-t border-gray-100">
                      <div className="grid md:grid-cols-2 gap-4 pt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Prezzo</label>
                          <Input value={svc.price_range || ''} onChange={(e) => updateService(idx, 'price_range', e.target.value)} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Durata (minuti)</label>
                          <Input type="number" value={svc.duration_minutes || 0} onChange={(e) => updateService(idx, 'duration_minutes', parseInt(e.target.value) || 0)} />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Descrizione (IT)</label>
                        <Textarea value={svc.description || ''} onChange={(e) => updateService(idx, 'description', e.target.value)} rows={6} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Descrizione (EN)</label>
                        <Textarea value={svc.description_en || ''} onChange={(e) => updateService(idx, 'description_en', e.target.value)} rows={6} />
                      </div>
                      <Button onClick={() => handleServiceUpdate(svc.id, idx)} disabled={savingService === idx} className="bg-[#0066b2] hover:bg-[#005494]" data-testid={`save-service-${idx}`}>
                        <Save className="w-4 h-4 mr-2" />
                        {savingService === idx ? 'Salvataggio...' : `Salva ${svc.name}`}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </TabsContent>

            {/* Images Tab */}
            <TabsContent value="images" className="space-y-6">
              <div className="bg-white rounded-2xl p-6 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Logo</h3>
                <Input value={content?.logo_url || ''} onChange={(e) => setContent({ ...content, logo_url: e.target.value })} placeholder="https://..." data-testid="logo-url-input" />
                {content?.logo_url && <img src={content.logo_url} alt="Logo" className="h-16 w-auto mt-3 border rounded p-2" />}
              </div>
              <div className="bg-white rounded-2xl p-6 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Immagini Team</h3>
                <div className="space-y-6">
                  {[
                    { key: 'team_mechanic_url', label: 'Team Meccanici' },
                    { key: 'team_revision_url', label: 'Team Revisioni' },
                    { key: 'team_office_url', label: 'Team Ufficio' },
                  ].map(({ key, label }) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
                      <Input value={content?.[key] || ''} onChange={(e) => setContent({ ...content, [key]: e.target.value })} placeholder="https://..." data-testid={`${key.replace(/_/g, '-')}-input`} />
                      {content?.[key] && <img src={content[key]} alt={label} className="mt-2 h-32 w-auto object-contain border rounded" />}
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Colors Tab */}
            <TabsContent value="colors" className="space-y-6">
              <div className="bg-white rounded-2xl p-6 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Palette Colori</h3>
                <p className="text-sm text-gray-600 mb-6">Modifica i colori principali del sito. Usa codici esadecimali (es: #0066b2)</p>
                <div className="grid md:grid-cols-3 gap-6">
                  {[
                    { key: 'primary_color', label: 'Colore Primario', default: '#0066b2' },
                    { key: 'secondary_color', label: 'Colore Secondario', default: '#005494' },
                    { key: 'accent_color', label: 'Colore Accento', default: '#0066b2' },
                  ].map(({ key, label, default: def }) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
                      <div className="flex gap-2">
                        <Input type="color" value={content?.[key] || def} onChange={(e) => setContent({ ...content, [key]: e.target.value })} className="w-20 h-12" data-testid={`${key.replace(/_/g, '-')}-input`} />
                        <Input value={content?.[key] || ''} onChange={(e) => setContent({ ...content, [key]: e.target.value })} placeholder={def} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <div className="mt-8 flex justify-end">
            <Button onClick={handleSave} disabled={saving} size="lg" className="bg-[#0066b2] hover:bg-[#005494]">
              <Save className="w-5 h-5 mr-2" />
              {saving ? 'Salvataggio...' : 'Salva Tutte le Modifiche'}
            </Button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
